import { useState } from "react";
import { useIntl } from "react-intl";
import IOnPreviewEmailTemplateDTO from "../../../../models/DTO/IOnPreviewEmailTemplateDTO";
import { IEmailTemplateSampleOrderDTO } from "../../../../models/DTO/IOnGetEmailTemplateSampleOrdersDTO";
import { EmailPreviewMode } from "../../../../hooks/business/useEmailTemplateEditor";

type Props = {
    preview: IOnPreviewEmailTemplateDTO | null;
    loading: boolean;
    error: string | null;
    mode: EmailPreviewMode;
    onModeChange: (mode: EmailPreviewMode) => void;
    scope: "order" | "account";
    /** Sample data and labels refer to quotes instead of orders. */
    isQuote?: boolean;
    sampleOrders: IEmailTemplateSampleOrderDTO[];
    sampleOrderGuid?: string;
    onSampleOrderChange: (guid: string | undefined) => void;
    onSearchSampleOrders: (q: string) => void;
};

/**
 * Server-rendered preview in a sandboxed iframe (srcDoc), switchable between a desktop and a
 * 375px phone width, rendered from a chosen sample order when the slot is order-scoped.
 */
const EmailPreviewPane = (props: Props) => {
    const intl = useIntl();
    const [query, setQuery] = useState("");

    return (
        <div className={"EmailPreviewPane"}>
            <div className={"preview-toolbar"}>
                <div className={"btn-group btn-group-sm"} role={"group"}>
                    <button
                        type={"button"}
                        className={`btn ${props.mode === "desktop" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => props.onModeChange("desktop")}>
                        <i className={"fad fa-desktop me-1"} aria-hidden={true} />
                        {intl.formatMessage({ id: "desktop" })}
                    </button>
                    <button
                        type={"button"}
                        className={`btn ${props.mode === "mobile" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => props.onModeChange("mobile")}>
                        <i className={"fad fa-mobile-alt me-1"} aria-hidden={true} />
                        {intl.formatMessage({ id: "mobile" })}
                    </button>
                </div>

                {props.scope === "order" ? (
                    <>
                        <input
                            type={"search"}
                            className={"form-control form-control-sm"}
                            style={{ maxWidth: 160 }}
                            placeholder={intl.formatMessage({ id: props.isQuote ? "search_quotes" : "search_orders" })}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    props.onSearchSampleOrders(query);
                                }
                            }}
                        />
                        <select
                            className={"form-select form-select-sm"}
                            style={{ maxWidth: 260 }}
                            value={props.sampleOrderGuid || ""}
                            aria-label={intl.formatMessage({ id: props.isQuote ? "sample_quote" : "sample_order" })}
                            onChange={(e) => props.onSampleOrderChange(e.target.value || undefined)}>
                            <option value={""}>
                                {intl.formatMessage({ id: props.isQuote ? "sample_quote_latest" : "sample_order_latest" })}
                            </option>
                            {props.sampleOrders.map((o) => (
                                <option key={o.guid} value={o.guid}>
                                    {o.name}
                                    {o.vendorName ? ` — ${o.vendorName}` : ""}
                                </option>
                            ))}
                        </select>
                    </>
                ) : null}

                {props.loading ? (
                    <span className={"text-muted small ms-auto"}>{intl.formatMessage({ id: "rendering_preview" })}</span>
                ) : null}
                {props.preview?.sampleSource === "synthetic" && props.scope === "order" ? (
                    <span className={"badge bg-secondary ms-auto"}>{intl.formatMessage({ id: "sample_data" })}</span>
                ) : null}
            </div>

            {props.error ? <div className={"alert alert-danger py-2"}>{props.error}</div> : null}

            {props.preview ? (
                <>
                    <div className={"preview-subject"}>
                        <span className={"text-muted small me-1"}>{intl.formatMessage({ id: "subject" })}:</span>
                        {props.preview.subject}
                    </div>
                    <div className={"preview-frame-wrapper"}>
                        <iframe
                            title={intl.formatMessage({ id: "preview" })}
                            className={props.mode}
                            sandbox={""}
                            srcDoc={props.preview.html}
                            data-testid={"email-preview-frame"}
                        />
                    </div>
                    {props.preview.warnings.length ? (
                        <ul className={"preview-warnings"}>
                            {props.preview.warnings.map((w, i) => (
                                <li key={i}>{intl.formatMessage({ id: w, defaultMessage: w })}</li>
                            ))}
                        </ul>
                    ) : null}
                </>
            ) : (
                <div className={"preview-frame-wrapper"}>
                    <div className={"text-muted p-4"}>
                        {intl.formatMessage({ id: props.loading ? "rendering_preview" : "no_preview_yet" })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailPreviewPane;
